import { useEffect, useLayoutEffect, useRef, useState } from 'react';

type Size = { height: string; paddingTop: string; paddingBottom: string };

const collapsed: Size = {
  height: '0px',
  paddingTop: '0px',
  paddingBottom: '0px',
};

const sizeOf = (element: HTMLElement): Size => {
  const style = window.getComputedStyle(element);
  return {
    height: style.height,
    paddingTop: style.paddingTop,
    paddingBottom: style.paddingBottom,
  };
};

/**
 * Shows and hides an element with a height slide, as the subject site's jQuery
 * slideDown, slideUp and slideToggle do. `shown` stays true until a close has
 * finished sliding, so the element keeps its open class while it slides shut.
 * A slide that is turned round part way carries on from where it had got to.
 */
export const useSlideToggle = <T extends HTMLElement>(
  open: boolean,
  duration: number
) => {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(open);
  const slidOpen = useRef(open);
  const running = useRef<Animation | null>(null);

  useEffect(
    () => () => {
      running.current?.cancel();
    },
    []
  );

  useLayoutEffect(() => {
    if (open === slidOpen.current) return;
    if (open && !shown) {
      // Render it shown first, so its full height can be measured
      setShown(true);
      return;
    }
    slidOpen.current = open;

    const element = ref.current;
    if (!element || typeof element.animate !== 'function') {
      setShown(open);
      return;
    }

    const previous = running.current;
    const from = previous ? sizeOf(element) : null;
    if (previous) {
      previous.onfinish = null;
      previous.cancel();
    }
    const expanded = sizeOf(element);

    element.style.overflow = 'hidden';
    const animation = element.animate(
      open ? [from ?? collapsed, expanded] : [from ?? expanded, collapsed],
      { duration, easing: 'ease-in-out' }
    );
    running.current = animation;
    animation.onfinish = () => {
      running.current = null;
      element.style.overflow = '';
      if (!open) setShown(false);
    };
  }, [open, shown, duration]);

  return { ref, shown };
};
